import { useEffect, useState, type RefObject } from 'react';
import { useScroll, useTransform } from 'motion/react';

const FALLBACK_HEADER_HEIGHT = 35;
const HEADER_GAP = 20;
// Releases the logo this far before the container ends.
const RELEASE_GAP = 45;

type PinMetrics = {
  /** Document scroll position where the logo reaches its resting offset. */
  start: number;
  /** Scroll distance the logo stays parked for. */
  distance: number;
};

/**
 * Holds the hero logo below the site header while the hero scrolls past, then
 * releases it. Translating an absolutely positioned logo keeps the video and
 * hero copy where they are; `position: sticky` would need it back in flow.
 */
export const usePinnedLogo = (
  logoRef: RefObject<HTMLElement | null>,
  containerRef: RefObject<HTMLElement | null>,
) => {
  const [{ start, distance }, setMetrics] = useState<PinMetrics>({
    start: 0,
    distance: 0,
  });
  const { scrollY } = useScroll();

  useEffect(() => {
    const logo = logoRef.current;
    const container = containerRef.current;
    if (!logo || !container) return undefined;

    const measure = () => {
      // Hidden at mobile widths, where the unpinned logo variant renders instead.
      if (!logo.offsetParent) {
        setMetrics({ start: 0, distance: 0 });
        return;
      }

      const headerHeight =
        (document.querySelector('header')?.offsetHeight ||
          FALLBACK_HEADER_HEIGHT) + HEADER_GAP;
      const logoText = document.querySelector<HTMLElement>('.seed-logo');
      const travel =
        container.offsetHeight -
        logo.offsetHeight -
        (logoText?.offsetHeight ?? 0) -
        headerHeight -
        RELEASE_GAP;

      setMetrics({
        // offsetTop rather than a rect, so a mid-pin remeasure isn't skewed by
        // the translate already applied to the logo.
        start:
          container.getBoundingClientRect().top +
          window.scrollY +
          logo.offsetTop -
          headerHeight,
        distance: Math.max(travel, 0),
      });
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [logoRef, containerRef]);

  // A zero-width input range would divide by zero; the output pins to 0 anyway.
  return useTransform(
    scrollY,
    [start, start + Math.max(distance, 1)],
    [0, distance],
    { clamp: true },
  );
};
